import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import Layout, { LangProvider } from './Layout';
import { ToastProvider } from './ui';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Contractors from './pages/Contractors';
import ContractorDetail from './pages/ContractorDetail';
import Workers from './pages/Workers';
import Stock from './pages/Stock';
import Attendance from './pages/Attendance';
import Requests from './pages/Requests';
import Expenses from './pages/Expenses';
import Salaries from './pages/Salaries';
import Project from './pages/Project';
import Settings from './pages/Settings';
import { MyWorkers, MyStock, MyAttendance as ContractorAttendance, MyBill } from './pages/ContractorPages';
import { MyAttendance as WorkerAttendance, MySalary, MyAdvances } from './pages/WorkerPages';

// Require auth (optionally a role). Renders children inside the app Layout.
function Protected({ children, roles }) {
  const { profile, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-400">লোড হচ্ছে…</div>;
  if (!profile) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(profile.user?.role)) return <Navigate to="/" replace />;
  return <Layout>{children({ role: profile.user.role })}</Layout>;
}

// Role-routed content: pick the right component by role.
function RolePage({ owner, manager, contractor, worker }) {
  const { profile } = useAuth();
  const role = profile?.user?.role;
  const map = { owner, manager, contractor, worker };
  const Comp = map[role];
  if (!Comp) return <Navigate to="/" replace />;
  return <Comp role={role} />;
}

export default function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <ToastProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          <Route path="/" element={<Protected>{() => <Dashboard />}</Protected>} />

          {/* Staff (owner + manager) */}
          <Route path="/contractors" element={<Protected roles={['owner', 'manager']}>{({ role }) => <Contractors role={role} />}</Protected>} />
          <Route path="/contractors/:id" element={<Protected roles={['owner', 'manager']}>{({ role }) => <ContractorDetail role={role} />}</Protected>} />
          <Route path="/workers" element={<Protected roles={['owner', 'manager', 'contractor']}>
            {() => <RolePage owner={Workers} manager={Workers} contractor={MyWorkers} />}
          </Protected>} />
          <Route path="/stock" element={<Protected roles={['owner', 'manager', 'contractor']}>
            {() => <RolePage owner={Stock} manager={Stock} contractor={MyStock} />}
          </Protected>} />
          <Route path="/attendance" element={<Protected roles={['owner', 'manager', 'contractor', 'worker']}>
            {() => <RolePage owner={Attendance} manager={Attendance}
              contractor={ContractorAttendance} worker={WorkerAttendance} />}
          </Protected>} />
          <Route path="/requests" element={<Protected roles={['owner', 'manager', 'contractor']}>
            {() => <RolePage owner={Requests} manager={Requests} contractor={Requests} />}
          </Protected>} />

          {/* Owner-only modules */}
          <Route path="/expenses" element={<Protected roles={['owner']}>{() => <Expenses />}</Protected>} />
          <Route path="/salaries" element={<Protected roles={['owner']}>{() => <Salaries />}</Protected>} />
          <Route path="/project" element={<Protected roles={['owner']}>{() => <Project />}</Protected>} />

          {/* Settings/Backup (owner + manager) */}
          <Route path="/settings" element={<Protected roles={['owner', 'manager']}>{() => <Settings />}</Protected>} />

          {/* Contractor */}
          <Route path="/bill" element={<Protected roles={['contractor']}>{() => <MyBill />}</Protected>} />

          {/* Worker */}
          <Route path="/salary" element={<Protected roles={['worker']}>{() => <MySalary />}</Protected>} />
          <Route path="/advances" element={<Protected roles={['worker']}>{() => <MyAdvances />}</Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
        </ToastProvider>
      </LangProvider>
    </AuthProvider>
  );
}